import React, { useState } from 'react';
import { X, Plus, Trash2, Upload, Download } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';

const BulkTireModal = ({ isOpen, onClose, onSuccess }) => {
  const [commonData, setCommonData] = useState({
    brand: '',
    model: '',
    size: '',
    tire_type: 'new',
    category: 'drive',
    purchase_date: '',
    purchase_price: '',
    expected_lifespan_km: '',
    current_tread_depth_mm: '',
    warehouse_location: ''
  });

  const [serialNumbers, setSerialNumbers] = useState('');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState([]);

  if (!isOpen) return null;

  const handleCommonDataChange = (field, value) => {
    setCommonData(prev => ({ ...prev, [field]: value }));
  };

  const generatePreview = () => {
    const numbers = serialNumbers
      .split('\n')
      .map(n => n.trim())
      .filter(n => n.length > 0);

    const previewData = numbers.map(serial => ({
      serial_number: serial,
      ...commonData
    }));

    setPreview(previewData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const numbers = serialNumbers
      .split('\n')
      .map(n => n.trim())
      .filter(n => n.length > 0);

    if (numbers.length === 0) {
      toast.error('Adicione pelo menos um número de série');
      return;
    }

    if (numbers.length > 100) {
      toast.error('Máximo de 100 pneus por vez');
      return;
    }

    const tires = numbers.map(serial => ({ serial_number: serial }));

    setLoading(true);

    try {
      const response = await api.post('/api/tires/bulk', {
        tires,
        commonData: {
          ...commonData,
          purchase_price: commonData.purchase_price ? parseFloat(commonData.purchase_price) : null,
          expected_lifespan_km: commonData.expected_lifespan_km ? parseInt(commonData.expected_lifespan_km) : null,
          current_tread_depth_mm: commonData.current_tread_depth_mm ? parseFloat(commonData.current_tread_depth_mm) : null
        }
      });

      const { success, failed, total } = response.data.data;

      if (failed > 0) {
        toast.error(`${success} pneus cadastrados, ${failed} com erro`);
        console.log('Erros:', response.data.data.errors);
      } else {
        toast.success(`${success} pneus cadastrados com sucesso!`);
      }

      if (success > 0) {
        onSuccess();
        resetForm();
        onClose();
      }
    } catch (error) {
      console.error('Erro ao cadastrar em lote:', error);
      toast.error(error.response?.data?.message || 'Erro ao cadastrar pneus');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCommonData({
      brand: '',
      model: '',
      size: '',
      tire_type: 'new',
      category: 'drive',
      purchase_date: '',
      purchase_price: '',
      expected_lifespan_km: '',
      current_tread_depth_mm: '',
      warehouse_location: ''
    });
    setSerialNumbers('');
    setPreview([]);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Cadastro em Lote
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Dados Comuns */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              Dados Comuns (aplicados a todos os pneus)
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Marca */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Marca *
                </label>
                <input
                  type="text"
                  required
                  value={commonData.brand}
                  onChange={(e) => handleCommonDataChange('brand', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: Michelin"
                />
              </div>

              {/* Modelo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Modelo *
                </label>
                <input
                  type="text"
                  required
                  value={commonData.model}
                  onChange={(e) => handleCommonDataChange('model', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: XZE 2+"
                />
              </div>

              {/* Tamanho */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Tamanho *
                </label>
                <input
                  type="text"
                  required
                  value={commonData.size}
                  onChange={(e) => handleCommonDataChange('size', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Ex: 295/80R22.5"
                />
              </div>

              {/* Categoria */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Categoria
                </label>
                <select
                  value={commonData.category}
                  onChange={(e) => handleCommonDataChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="steer">Direção</option>
                  <option value="drive">Tração</option>
                  <option value="trailer">Reboque</option>
                  <option value="all_position">Todas Posições</option>
                </select>
              </div>

              {/* Data de Compra */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data de Compra
                </label>
                <input
                  type="date"
                  value={commonData.purchase_date}
                  onChange={(e) => handleCommonDataChange('purchase_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>

              {/* Preço Unitário */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Preço Unitário (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={commonData.purchase_price}
                  onChange={(e) => handleCommonDataChange('purchase_price', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="2850.00"
                />
              </div>
            </div>
          </div>

          {/* Números de Série */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Números de Série * (um por linha)
            </label>
            <textarea
              required
              value={serialNumbers}
              onChange={(e) => setSerialNumbers(e.target.value)}
              onBlur={generatePreview}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm"
              placeholder={'BR001234567\nBR001234568\nBR001234569\n...'}
            />
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {serialNumbers.split('\n').filter(n => n.trim()).length} pneus
            </p>
          </div>

          {/* Preview */}
          {preview.length > 0 && (
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Preview ({preview.length} pneus)
              </h3>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4 max-h-40 overflow-y-auto">
                <div className="space-y-2">
                  {preview.slice(0, 5).map((tire, idx) => (
                    <div key={idx} className="text-sm text-gray-700 dark:text-gray-300">
                      <span className="font-mono">{tire.serial_number}</span> -{' '}
                      {tire.brand} {tire.model} {tire.size}
                    </div>
                  ))}
                  {preview.length > 5 && (
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      ... e mais {preview.length - 5} pneus
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading ? 'Cadastrando...' : `Cadastrar ${serialNumbers.split('\n').filter(n => n.trim()).length} Pneus`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkTireModal;
